import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Menu, User, LogOut, LayoutDashboard, FileText, Package, X, Moon, Sun } from "lucide-react";
import { useState } from "react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <nav className="bg-[var(--pym-bg)] border-b border-[var(--pym-border)] sticky top-0 z-40" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src="https://customer-assets.emergentagent.com/job_promo-quote/artifacts/wgwlu5pd_WhatsApp%20Image%202026-03-27%20at%2018.43.52.jpeg" alt="PYM Promocionales" className="h-14 w-auto" />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-[#475569] hover:text-[#0F172A] transition-colors" data-testid="nav-home">Inicio</Link>
            <Link to="/cotizar" className="text-sm font-medium text-[#475569] hover:text-[#0F172A] transition-colors" data-testid="nav-cotizar">Cotizar</Link>
            {user && user.role === "admin" && (
              <Link to="/admin" className="text-sm font-medium text-[#0047AB] hover:text-[#003A8C] transition-colors" data-testid="nav-admin">Admin</Link>
            )}
            {user && user.role === "client" && (
              <Link to="/portal" className="text-sm font-medium text-[#0047AB] hover:text-[#003A8C] transition-colors" data-testid="nav-portal">Mi Portal</Link>
            )}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {/* Dark mode toggle */}
            <Button variant="ghost" size="sm" onClick={toggle} className="rounded-sm p-2 text-[var(--pym-text-sec)] hover:text-[var(--pym-text)] hover:bg-[var(--pym-hover)]" data-testid="theme-toggle">
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>

            {!user ? (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-sm font-semibold text-[#475569] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-sm hidden sm:inline-flex" data-testid="nav-login-btn">Iniciar Sesión</Button>
                </Link>
                <Link to="/cotizar">
                  <Button className="bg-[#E30613] text-white font-bold rounded-sm px-5 py-2 hover:bg-[#C80410] transition-colors text-sm" data-testid="nav-cta-btn">Cotiza Ahora</Button>
                </Link>
              </>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="rounded-sm flex items-center gap-2" data-testid="nav-user-menu">
                    <User className="w-4 h-4" />
                    <span className="text-sm font-medium hidden sm:inline">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {user.role === "admin" ? (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Admin Panel
                    </DropdownMenuItem>
                  ) : (
                    <>
                      <DropdownMenuItem onClick={() => navigate("/portal")} data-testid="menu-dashboard">
                        <LayoutDashboard className="w-4 h-4 mr-2" /> Mi Portal
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/portal/cotizaciones")} data-testid="menu-quotes">
                        <FileText className="w-4 h-4 mr-2" /> Cotizaciones
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/portal/pedidos")} data-testid="menu-orders">
                        <Package className="w-4 h-4 mr-2" /> Pedidos
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {/* Mobile menu */}
            <Button variant="ghost" className="md:hidden rounded-sm p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
        {/* Mobile dropdown */}
        {mobileOpen && (
          <div className="md:hidden border-t border-[#E2E8F0] py-3 space-y-2">
            <Link to="/" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm font-medium text-[#475569] hover:bg-[#F1F5F9] rounded-sm">Inicio</Link>
            <Link to="/cotizar" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm font-medium text-[#475569] hover:bg-[#F1F5F9] rounded-sm">Cotizar</Link>
            {!user && <Link to="/login" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm font-medium text-[#475569] hover:bg-[#F1F5F9] rounded-sm">Iniciar Sesión</Link>}
            {user && user.role === "admin" && <Link to="/admin" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm font-medium text-[#0047AB] hover:bg-[#F1F5F9] rounded-sm">Admin</Link>}
            {user && user.role === "client" && <Link to="/portal" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm font-medium text-[#0047AB] hover:bg-[#F1F5F9] rounded-sm">Mi Portal</Link>}
          </div>
        )}
      </div>
    </nav>
  );
}
